import { ReactNode } from "react";

import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppButton from "@/common/components/Button/AppButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import { ROUTE_PATH } from "@/common/constants/routing";
import { assertNever } from "@/common/ts/typeHelpers";
import {
  AppPermission,
  ImportType,
  OnboardingStepDefinitionDto,
  OnboardingStepType,
} from "@/core/api/generated/v1";

interface TheButtonProps {
  permissions?: AppPermission[];
  permissionsAny?: AppPermission[];
  to?: string;
  endIcon?: ReactNode;
  children?: ReactNode;
  onClick?: () => void | Promise<void>;
}

export function TheButton({
  permissions,
  permissionsAny,
  to,
  endIcon,
  children,
  onClick,
}: TheButtonProps) {
  return (
    <AuthorizedElement permissions={permissions} permissionsAny={permissionsAny}>
      <AppButton
        variant='outlined'
        size='medium'
        color='text'
        endIcon={endIcon || <AppIcon of='arrowForward' />}
        to={to}
        onClick={onClick}
      >
        {children}
      </AppButton>{" "}
    </AuthorizedElement>
  );
}

interface Props {
  stepDefinition: OnboardingStepDefinitionDto;
  onClick?: () => void | Promise<void>;
}

/** Leads to target action based on step type. */
export function OnboardingStepTargetActionButton({ stepDefinition, onClick }: Props) {
  const baseButtonProps: Partial<TheButtonProps> = {
    onClick: onClick,
  };

  if (!stepDefinition.type) {
    return null;
  }

  switch (stepDefinition.type) {
    case OnboardingStepType.None:
      return null;

    case OnboardingStepType.TenantSettingsUpdate:
      return (
        <TheButton
          permissions={[AppPermission.TenantManage]}
          to={ROUTE_PATH.TENANT_SETTINGS_EDIT}
          {...baseButtonProps}
        >
          Go to settings
        </TheButton>
      );
    case OnboardingStepType.DepartmentCreate:
      return (
        <TheButton
          permissions={[AppPermission.DepartmentManage]}
          to={ROUTE_PATH.DEPARTMENT_CREATE}
          {...baseButtonProps}
        >
          Create department
        </TheButton>
      );
    case OnboardingStepType.LocationCreate:
      return (
        <TheButton
          permissions={[AppPermission.LocationManage]}
          to={ROUTE_PATH.LOCATION_CREATE}
          {...baseButtonProps}
        >
          Create location
        </TheButton>
      );
    case OnboardingStepType.SpotCreate:
      return (
        <TheButton
          permissions={[AppPermission.SpotManage]}
          to={ROUTE_PATH.SPOT_CREATE()}
          {...baseButtonProps}
        >
          Create spot
        </TheButton>
      );
    case OnboardingStepType.RoleCreate:
      return (
        <TheButton
          permissions={[AppPermission.TenantRoleManage]}
          to={ROUTE_PATH.TENANT_ROLE_CREATE()}
          {...baseButtonProps}
        >
          Create role
        </TheButton>
      );
    case OnboardingStepType.InviteCreatePersonal:
      return (
        <TheButton
          permissions={[AppPermission.TenantInviteManage]}
          to={ROUTE_PATH.MANAGEMENT_INVITES_CREATE_PERSONAL()}
          {...baseButtonProps}
        >
          Create personal invite
        </TheButton>
      );
    case OnboardingStepType.InviteCreateAnonymous:
      return (
        <TheButton
          permissions={[AppPermission.TenantInviteManage]}
          to={ROUTE_PATH.MANAGEMENT_INVITES_CREATE_ANONYMOUS()}
          {...baseButtonProps}
        >
          Create anonymous invite
        </TheButton>
      );
    case OnboardingStepType.InviteAccept:
      return null;
    case OnboardingStepType.VehicleCreate:
      return (
        <TheButton
          permissions={[AppPermission.VehicleManage]}
          to={ROUTE_PATH.VEHICLE_CREATE}
          {...baseButtonProps}
        >
          Create vehicle
        </TheButton>
      );
    case OnboardingStepType.VehicleCreateMany:
      return (
        <TheButton
          permissions={[AppPermission.VehicleManage]}
          to={ROUTE_PATH.VEHICLE_CREATE_MANY_SIMILAR}
          {...baseButtonProps}
        >
          Create vehicles
        </TheButton>
      );
    case OnboardingStepType.VehicleImport:
      return (
        <TheButton
          permissions={[AppPermission.ImportManage]}
          to={ROUTE_PATH.MANAGEMENT_IMPORTS_CREATE({ type: ImportType.Vehicle })}
          {...baseButtonProps}
        >
          Import vehicles
        </TheButton>
      );
    case OnboardingStepType.VehicleUpdateAffiliation:
      return (
        <TheButton
          permissions={[AppPermission.VehicleRead]}
          to={ROUTE_PATH.VEHICLES}
          {...baseButtonProps}
        >
          Update vehicle affiliation
        </TheButton>
      );
    case OnboardingStepType.CustomerCreate:
      return (
        <TheButton
          permissions={[AppPermission.CustomerManage]}
          to={ROUTE_PATH.CUSTOMER_CREATE}
          {...baseButtonProps}
        >
          Create customer
        </TheButton>
      );
    case OnboardingStepType.ContractCreate:
      return (
        <TheButton
          permissions={[AppPermission.ContractManage]}
          to={ROUTE_PATH.CONTRACT_CREATE()}
          {...baseButtonProps}
        >
          Create contract
        </TheButton>
      );
    case OnboardingStepType.VisualInspectionCreate:
      return (
        <TheButton
          permissionsAny={[
            AppPermission.VisualInspectionPerform,
            AppPermission.VisualInspectionManage,
          ]}
          to={ROUTE_PATH.VISUAL_INSPECTION_CREATE()}
          {...baseButtonProps}
        >
          Create visual inspection
        </TheButton>
      );
    case OnboardingStepType.VisualInspectionCreateOnMobile:
      return null;
    case OnboardingStepType.DamageDetectionCreate:
      return (
        <TheButton
          permissionsAny={[
            AppPermission.DamageDetectionPerform,
            AppPermission.DamageDetectionManage,
          ]}
          to={ROUTE_PATH.DAMAGE_DETECTION_CREATE()}
          {...baseButtonProps}
        >
          Create damage detection
        </TheButton>
      );
    case OnboardingStepType.DamageDetectionCreateOnMobile:
      return null;
    case OnboardingStepType.DamageCostEvaluationCreate:
      return (
        <TheButton
          permissionsAny={[
            AppPermission.DamageCostEvaluationPerform,
            AppPermission.DamageCostEvaluationManage,
          ]}
          to={ROUTE_PATH.DAMAGE_COST_EVALUATION_CREATE()}
          {...baseButtonProps}
        >
          Create damage cost evaluation
        </TheButton>
      );
    case OnboardingStepType.PoolCreate:
      return (
        <TheButton
          permissions={[AppPermission.PoolManage]}
          to={ROUTE_PATH.POOL_CREATE}
          {...baseButtonProps}
        >
          Create pool
        </TheButton>
      );
    case OnboardingStepType.PoolAddItems:
      return (
        <TheButton
          permissions={[AppPermission.PoolManage]}
          to={ROUTE_PATH.POOLS}
          {...baseButtonProps}
        >
          Add pool items
        </TheButton>
      );
    case OnboardingStepType.ProductLocationCreate:
      return (
        <TheButton
          permissions={[AppPermission.ProductLocationManage]}
          to={ROUTE_PATH.PRODUCT_SALES_LOCATION_CREATE}
          {...baseButtonProps}
        >
          Create sales location
        </TheButton>
      );
    case OnboardingStepType.AssetSubscriptionPlanCreate:
      return (
        <TheButton
          permissions={[AppPermission.AssetSubscriptionPlanManage]}
          to={ROUTE_PATH.ASSET_SUBSCRIPTION_PLAN_CREATE}
          {...baseButtonProps}
        >
          Create asset subscription plan
        </TheButton>
      );
    case OnboardingStepType.AssetSubscriptionCreate:
      return (
        <TheButton
          permissions={[AppPermission.AssetSubscriptionManage]}
          to={ROUTE_PATH.ASSET_SUBSCRIPTION_CREATE()}
          {...baseButtonProps}
        >
          Create asset subscription
        </TheButton>
      );
    case OnboardingStepType.RepairSpecCreate:
      return (
        <TheButton
          permissions={[AppPermission.PoolManage]}
          to={ROUTE_PATH.POOL_CREATE}
          {...baseButtonProps}
        >
          Create pool
        </TheButton>
      );
    default:
      assertNever(stepDefinition.type);
  }
}
